import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DropdownItem, DropdownMenu,
    DropdownToggle, Nav, NavItem,
    Spinner, UncontrolledDropdown,
    UncontrolledTooltip } from 'reactstrap';
import classNames from 'classnames';
import { filter, isEmpty, isNull, map, uniqueId } from 'lodash';
import get from 'lodash/get';
import queryString from 'query-string';

import logoVigor from 'assets/img/brand/logo.png';
import logoN1tro from 'assets/img/brand/n1tro/logo.png';
import logoTMGaming from 'assets/img/brand/tmgaming/logo.png';

import { AppNavbarBrand, AppSidebarToggler } from '@coreui/react';

import { Box } from '@mui/material';

import { useNotification } from 'context/NotificationContext';

import useRouter from 'common/hooks/useRouter';
import { isEngineN1tro, isEngineTmgaming, isLottery } from 'common/shared';

import MemberAPI from 'apis/MemberAPI';
import TopBarAPI from 'apis/TopBarAPI';

import MultipleMarquee from 'components/MultipleMarquee';
import { accountType } from 'components/utils/function';

import DefaultHeaderDropdown from './DefaultHeaderDropdown';

import './styles.scss';

const DefaultHeader = (props) => {
    const { t } = useTranslation();
    const router = useRouter();
    const [notification, dispatchNotification] = useNotification();
    const user = useSelector(state => state.auth?.user || {});

    const [data, setData] = useState({});
    const [logoHeader, setLogoHeader] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [unreadTrackingClonedList, setUnreadTrackingClonedList] = useState([]);
    const [isOpen, setIsOpen] = useState(false);

    const showNotification = useMemo(() => ({
        deposit: get(data, 'deposit_notification_enable', false),
        withdraw: get(data, 'withdraw_notification_enable', false),
        memo: get(data, 'memo_notification_enable', false),
        activity: get(data, 'activity_notification_enable', false),
        subAccount: get(data, 'sub_account_activity_notification_enable', false),
        autoBank: get(data, 'autobank_notification_enable', false),
        pagaWithdraw: get(data, 'paga_withdraw_count_enable', false),
        kyc: get(data, 'kyc_notification_enable', false),
        trackingClonedEnable: get(data, 'tracking_cloned_count_enable', false),
    }), [data]);

    const getTopBarMenu = useCallback(async () => {
        setIsLoading(true);
        await TopBarAPI.getTopBarMenu().then(res => {
            setData(res);
            dispatchNotification({
                type: 'update_notification',
                deposit: res.deposit,
                withdraw: res.withdraw,
                memo: res.memo,
                paga_withdraw_count: res.paga_withdraw_count,
                kyc: res.kyc_pending_count,
                tracking_cloned_count: res.tracking_cloned_count,
                running_text: res.running_text,
            });
        });
        setIsLoading(false);
    }, []);
    const getUnreadTrackingClonedList = useCallback(async () => {
        await MemberAPI.getUnreadTrackingClonedList().then(res => {
            setIsLoading(true);
            setIsOpen(true);

            setUnreadTrackingClonedList(res?.data || []);
        }).catch(e => {
            toast.error(t(e.message));
        });
        setIsLoading(false);
    }, []);

    useEffect(() => {
        getTopBarMenu();
    }, [getTopBarMenu]);


    useEffect(() => {
        if (user?.master_whitelabel_logo) {
            setLogoHeader(user.master_whitelabel_logo);
        } else {
            if (isEngineTmgaming) {
                setLogoHeader(logoTMGaming);
            } else if (isEngineN1tro) {
                setLogoHeader(logoN1tro);
            } else {
                setLogoHeader(logoVigor);
            }
        }
    }, [user]);

    const goToLogPage = (newUserName) => {
        let queryStringify = `?${queryString.stringify({ username: '' })}`;
        const query = {
            username: newUserName || user.username
        };
        if (!isNull(newUserName)) {
            queryStringify = `?${queryString.stringify(query)}`;
        }
        if (accountType() === 'Admin') {
            router.history.push({
                pathname: '/action-log/admin-logs',
                search: queryStringify,
            });
        } else if (accountType() === 'Company') {
            router.history.push({
                pathname: '/action-log/c-company-logs',
                search: queryStringify,
            });
        } else if (accountType() === 'Master') {
            router.history.push({
                pathname: '/action-log/master-whitelabel-logs',
                search: queryStringify,
            });
        } else if (accountType() === 'Whitelabel') {
            router.history.push({
                pathname: '/action-log/c-whitelabel-logs',
                search: queryStringify
            });
        }
    };

    const renderDeposit = () => (<h4 className='notification-wrapper mx-2 mb-0' id="depositHeader">
        <Link to='/request-management/deposit'>
            <i className="fas fa-hand-holding-usd"/>
            <UncontrolledTooltip placement="bottom" target="depositHeader">
                {t('Deposit')}
            </UncontrolledTooltip>
        </Link>
        {notification.deposit > 0 && <div
            className='notification-counter bg-danger text-white'>{notification.deposit}</div>}
    </h4>);

    const renderWithdraw = () => (<h4 className='notification-wrapper mx-2 mb-0'
        id="withdrawHeader">
        <Link to='/request-management/withdraw'>
            <i className="fas fa-money-bill-wave"/>
            <UncontrolledTooltip placement="bottom" target="withdrawHeader">
                {t('Withdraw')}
            </UncontrolledTooltip>
        </Link>
        {notification.withdraw > 0 && <div
            className='notification-counter bg-danger text-white'>{notification.withdraw}</div>}
    </h4>);

    const renderPagaWithdraw = () => (<h4 className='notification-wrapper mx-2 mb-0'
        id="withdrawInstantdepositHeader">
        <Link to='/request-management/unopay-withdraw'>
            <i className="fas fa-money-bill-alt" />
            <UncontrolledTooltip placement="bottom" target="withdrawInstantdepositHeader">
                {t('Withdraw Instant')}
            </UncontrolledTooltip>
        </Link>
        {notification.pagaWithdraw > 0 && <div
            className='notification-counter bg-danger text-white'>{notification.pagaWithdraw}</div>}
    </h4>);

    const renderKYC = () => {
        const kycRedirectLink = () => {
            if (accountType() === 'Admin') {
                return '/global-member/list?kyc_status=kyc_pending';
            } else {
                return '/member/list?kyc_status=kyc_pending';
            }
        };

        return (<h4 className='notification-wrapper mx-2 mb-0'
            id="kycHeader">
            <Link to={kycRedirectLink}>
                <i className="fa fa-user"/>
                <UncontrolledTooltip placement="bottom" target="kycHeader">
                    {t('kycPending')}
                </UncontrolledTooltip>
            </Link>
            {notification.kyc > 0 && <div
                className='notification-counter bg-danger text-white'>{notification.kyc}</div>}
        </h4>);
    };

    const renderMemo = () => (<h4 className='notification-wrapper mx-2 mb-0'
        id="memoHeader">
        <Link to='/memo/memo'>
            <i className="fa fa-envelope"/>
            <UncontrolledTooltip placement="bottom" target="memoHeader">
                {t('memo')}
            </UncontrolledTooltip>
        </Link>
        {notification.memo > 0 && <div
            className='notification-counter bg-danger text-white'>{notification.memo}</div>}
    </h4>);

    const renderActivity = () => {
        const activity = get(data, 'activity', {});

        return (
            <div className='mx-2'>
                <UncontrolledDropdown className='cursor-pointer'>
                    <DropdownToggle tag="div">
                        <h4 id="activityHeader" className='notification-wrapper mb-0'>
                            <i className="fas fa-clipboard-list" />
                            <UncontrolledTooltip placement="bottom" target="activityHeader">
                                {t('Activity')}
                            </UncontrolledTooltip>
                        </h4>
                    </DropdownToggle>
                    <DropdownMenu right>
                        <DropdownItem header>{t('Activity')}</DropdownItem>
                        {isLoading && (
                            <div className='d-flex justify-content-center my-2'>
                                <Spinner/>
                            </div>
                        )}
                        {!isLoading && <>
                            {
                                !!get(activity, 'last_failed_login.date_time') && <DropdownItem onClick={(e) => {
                                    e.preventDefault();
                                    goToLogPage();
                                }}>
                                    <h6 className='mb-0'>
                                        <Trans i18nKey='Last Failed Login From' values={{ ip: activity.last_failed_login.ip }} />
                                    </h6>
                                    <small>{activity.last_failed_login.date_time}</small>
                                </DropdownItem>
                            }
                            {
                                !!(+activity.count_failed_login) && <DropdownItem onClick={(e) => {
                                    e.preventDefault();
                                    goToLogPage();
                                }}>
                                    <h6 className='mb-0'>
                                        <Trans i18nKey='Failed Attempts Since Last Login' values={{ times: activity.count_failed_login }}/>
                                    </h6>
                                </DropdownItem>
                            }
                            {
                                !!get(activity, 'last_login.date_time') && <DropdownItem onClick={(e) => {
                                    e.preventDefault();
                                    goToLogPage();
                                }}>
                                    <h6 className='mb-0'>
                                        <Trans i18nKey='Last Login From' values={{ ip: activity.last_login.ip }} />
                                    </h6>
                                    <small>{activity.last_login.date_time}</small>
                                </DropdownItem>
                            }
                        </>
                        }
                        <DropdownItem onClick={(e) => {
                            e.preventDefault();
                            goToLogPage();
                        }}>
                            <div className='text-center'>{t('All Activities')}</div>
                        </DropdownItem>
                    </DropdownMenu>
                </UncontrolledDropdown>
            </div>

        );
    };

    const renderSubActivity = () => {
        const actionLog = get(data, 'action_log', {});
        const totalNoti = get(actionLog, 'total', 0);
        const notifyActionLogs = get(actionLog, 'notify_action_logs', []);
        return (
            <div className='mx-2' onClick={() => {
                if (get(data, 'action_log.notify_action_logs', []).length
                !== get(notification, 'notifyActionLog')) {
                    getTopBarMenu();
                }
            }}>
                <UncontrolledDropdown className='cursor-pointer'>
                    <DropdownToggle tag="div">
                        <h4 id="subActivityHeader" className='notification-wrapper mb-0'>
                            <i className="fas fa-exclamation-triangle" />
                            <UncontrolledTooltip placement="bottom" target="subActivityHeader">
                                {t('subAccountActivity')}
                            </UncontrolledTooltip>
                            {totalNoti > 0 && (
                                <div
                                    className='notification-counter bg-danger text-white'>{totalNoti}</div>
                            )}
                        </h4>
                    </DropdownToggle>
                    <DropdownMenu right>
                        <DropdownItem header>{t('subAccountActivity')}</DropdownItem>

                        {isLoading && (
                            <div className='d-flex justify-content-center my-2'>
                                <Spinner/>
                            </div>
                        )}

                        {!isLoading && !isEmpty(notifyActionLogs) && notifyActionLogs?.map((notify, index) => {
                            const { account_name, display_name, log_date, activity } = notify;
                            const isLastElement = notifyActionLogs?.length - 1 === index;
                            return (
                                <DropdownItem className={classNames({
                                    'last-element': isLastElement,
                                })} onClick={(e) => {
                                    e.preventDefault();
                                    goToLogPage(account_name);
                                }} key={uniqueId()}>
                                    <Box className="d-flex align-items-center">
                                        <Box className='mb-0 font-weight-bold'>
                                            {account_name} {display_name && `(${ display_name })`}:
                                        </Box>
                                        <div className='ml-1'> {activity}</div>
                                    </Box>
                                    <small>{log_date}</small>

                                </DropdownItem>
                            );
                        })}

                        <DropdownItem className='btn-all' onClick={(e) => {
                            e.preventDefault();
                            goToLogPage(null);
                        }}>
                            <div className='text-center'>{t('All Activities')}</div>
                        </DropdownItem>
                    </DropdownMenu>
                </UncontrolledDropdown>
            </div>
        );
    };

    const renderAutoBank = () => {
        const autoBank = get(data, 'autobank_notification', {});
        const totalNoti = get(autoBank, 'total', 0);
        const listAutoBankNotification = get(autoBank, 'list_autobank_notification', []);
        return (
            <div className='mx-2' onClick={() => {
                if (get(data, 'autobank_notification.list_autobank_notification', []).length
                !== get(notification, 'autoBankNotification')) {
                    getTopBarMenu();
                }
            }}>
                <UncontrolledDropdown className='cursor-pointer'>
                    <DropdownToggle tag="div">
                        <h4 id="Autobank" className='notification-wrapper mb-0'>
                            <i className="fa fa-university" />
                            <UncontrolledTooltip placement="bottom" target="Autobank">
                                {t('Autobank')}
                            </UncontrolledTooltip>
                            {totalNoti > 0 && (
                                <div
                                    className='notification-counter bg-danger text-white'>{totalNoti}</div>
                            )}
                        </h4>
                    </DropdownToggle>
                    <DropdownMenu right>
                        <div className='position-relative menu-autobank'>
                            <DropdownItem className={classNames({
                                'empty-data': isEmpty(listAutoBankNotification)
                            })} header>{t('Autobank')}</DropdownItem>

                            {isLoading && (
                                <div className='d-flex justify-content-center my-2'>
                                    <Spinner/>
                                </div>
                            )}
                            {!isLoading && (!isEmpty(listAutoBankNotification) ? listAutoBankNotification?.map((notify, index) => {
                                const { autobank_username, bank_account_name, log_date, bank_account_status } = notify;
                                const isLastElement = listAutoBankNotification?.length - 1 === index;
                                return (
                                    <DropdownItem className={classNames({
                                        'last-element': isLastElement,
                                    })} onClick={(e) => {
                                        e.preventDefault();
                                        router.history.push({ pathname: '/bank-management/account/list' });
                                    }} key={uniqueId()}>
                                        <Box>
                                            <Box className='mb-0'>
                                                {autobank_username || ''} {bank_account_name || ''}
                                            </Box>
                                            <div className='menu__item_desc'> {bank_account_status}</div>
                                        </Box>
                                        <small>{log_date}</small>
                                    </DropdownItem>
                                );
                            }) : (
                                <DropdownItem />
                            ))}
                        </div>
                    </DropdownMenu>
                </UncontrolledDropdown>
            </div>
        );
    };

    const renderTrackingClonedMember = () => {
        const totalCount = notification.trackingClonedCount;

        return (
            <div className='mx-2'>
                <UncontrolledDropdown className='cursor-pointer'>
                    <DropdownToggle tag="div" onClick={() => {
                        if (!isOpen) {
                            getUnreadTrackingClonedList();
                        }
                        setIsOpen(!isOpen);
                    }}>
                        <h4 id="trackingClonedMemberHeader" className='notification-wrapper mb-0'>
                            <i className="fa fa-user-secret"/>
                            <UncontrolledTooltip placement="bottom" target="trackingClonedMemberHeader">
                                {t('Tracking Cloned Members')}
                            </UncontrolledTooltip>
                            {totalCount > 0 && (
                                <div
                                    className='notification-counter bg-danger text-white'>{totalCount}</div>
                            )}
                        </h4>
                    </DropdownToggle>
                    <DropdownMenu right style={{ minWidth: 300 }}>
                        <div className='menu-tracking-cloned-member'>
                            <DropdownItem header>{t('Tracking Cloned Members')}</DropdownItem>

                            {isLoading && (
                                <div className='d-flex justify-content-center my-2'>
                                    <Spinner/>
                                </div>
                            )}

                            {map(unreadTrackingClonedList, (item, index) => {
                                const isLastElement = unreadTrackingClonedList?.length - 1 === index;

                                let content = '';

                                if (item.ip_address) {
                                    content = `${t('Same Login IP')} ${item.ip_address} -  ${item.member_username}`;
                                } else {
                                    content = `${t('Same Bank Account')} ${item.bank_account_name} -  ${item.member_username}`;
                                }

                                return (
                                    <DropdownItem className={classNames({
                                        'last-element': isLastElement,
                                        'is-not-read-item': !item.is_read,
                                    })} onClick={(e) => {
                                        e.preventDefault();
                                        router.history.push({
                                            pathname: '/member/tracking-cloned',
                                            search: !item.ip_address ? '?filter_type=bank_account' : ''
                                        });
                                    }} key={uniqueId()}>
                                        <Box className='mb-0 font-weight-bold'>
                                            {content}
                                        </Box>
                                        <small>{item.created_at}</small>

                                    </DropdownItem>
                                );
                            })}

                            <DropdownItem className='btn-all' onClick={(e) => {
                                e.preventDefault();
                                router.history.push({
                                    pathname: '/member/tracking-cloned',
                                });
                            }}>
                                <div className='text-center'>{t('Tracking List')}</div>
                            </DropdownItem>
                        </div>
                    </DropdownMenu>
                </UncontrolledDropdown>
            </div>
        );
    };

    const limitCreditNotifications = useMemo(() => {
        const listFilter = filter(notification.runningText, rt => rt.field === 'limit_credit');
        return map(listFilter, item => {
            if (item.percentage === 100) {
                return t('limitCreditBlockedNotification', item);
            }
            return t('limitCreditNotification', item);
        });
    }, [notification.runningText, t]);

    return (
        <div className='d-flex align-items-center gap-3 h-100 w-100 justify-content-between header-wrapper'>
            <div className="d-flex align-items-center left-header w-100 overflow-hidden">
                <AppSidebarToggler className="d-lg-none align-items-center" display="md" mobile />
                <div className="d-flex align-items-center">
                    <AppNavbarBrand
                        className={!isLottery ? 'appNavbar-mobile' : ''}
                        full={{ src: logoHeader, width: 'auto', height: 35,
                            alt: isLottery ? 'Lottery Logo' : 'Wallet Logo' }}
                    />
                    <AppSidebarToggler className="d-md-down-none" display="lg" />
                </div>

                {!isEmpty(limitCreditNotifications) && (
                    <MultipleMarquee
                        list={limitCreditNotifications}/>
                )}

            </div>

            {/* notification icon */}
            <div className={'group-menu h-100 active-running-text'}>
                <div className="d-flex align-items-end group-menu-list">
                    {showNotification.deposit && notification.deposit !== undefined && renderDeposit()}
                    {showNotification.withdraw && notification.withdraw !== undefined && renderWithdraw()}
                    {showNotification.pagaWithdraw && notification.pagaWithdraw !== undefined && renderPagaWithdraw()}
                    {showNotification.kyc && notification.kyc !== undefined && renderKYC()}
                    {showNotification.memo && notification.memo !== undefined && renderMemo()}
                    {showNotification.activity && renderActivity()}
                    {showNotification.subAccount && renderSubActivity()}
                    {showNotification.autoBank && renderAutoBank()}
                    {showNotification.trackingClonedEnable && renderTrackingClonedMember()}
                </div>

                <Nav className="d-md-down-none info-use" navbar>
                    <NavItem className="px-3 d-none" >
                        <NavLink to="/dashboard" className="nav-link" >Dashboard</NavLink>
                    </NavItem>
                    <NavItem className="px-3 d-none">
                        <NavLink to="/users" className="nav-link">Users</NavLink>
                    </NavItem>
                    <NavItem className="px-3 d-none">
                        <NavLink to="#" className="nav-link">When things go wrong. Disable cache !!!!!</NavLink>
                    </NavItem>
                </Nav>
                <Nav className="" navbar>
                    <DefaultHeaderDropdown
                        goToProfile={props.goToProfile}
                        onLogout={props.onLogout}
                        currentUser={props.currentUser}
                        accnt
                    />
                </Nav>
            </div>
        </div>
    );
};

export default DefaultHeader;
